import type { JsonSchemaNode } from "@nmblr-platform-flow/schema";
import type { TemplateVariable } from "../../lib/schema-paths.js";
import { ArrayFields } from "./fields/ArrayFields.js";
import { BooleanToggle } from "./fields/BooleanToggle.js";
import { MultilineInput } from "./fields/MultilineInput.js";
import { NumberInput } from "./fields/NumberInput.js";
import { ObjectFields } from "./fields/ObjectFields.js";
import { SecretInput } from "./fields/SecretInput.js";
import { SelectInput } from "./fields/SelectInput.js";
import { StringInput } from "./fields/StringInput.js";
import { CodeEditor } from "./fields/index.js";

export interface SchemaFieldProps {
	name: string;
	schema: JsonSchemaNode;
	value: unknown;
	onChange: (value: unknown) => void;
	required?: boolean;
	templateVariables?: TemplateVariable[];
}

export function SchemaField({
	name,
	schema,
	value,
	onChange,
	required,
	templateVariables,
}: SchemaFieldProps) {
	// Extension dispatch (highest priority)
	if (schema["x-forge-code-language"]) {
		return (
			<CodeEditor
				name={name}
				schema={schema}
				value={value}
				onChange={onChange}
				required={required}
				templateVariables={templateVariables}
			/>
		);
	}
	if (schema["x-forge-secret"]) {
		return (
			<SecretInput
				name={name}
				schema={schema}
				value={value}
				onChange={onChange}
				required={required}
			/>
		);
	}
	if (schema["x-forge-multiline"]) {
		return (
			<MultilineInput
				name={name}
				schema={schema}
				value={value}
				onChange={onChange}
				required={required}
				templateVariables={templateVariables}
			/>
		);
	}

	// Enum → select regardless of type
	if (schema.enum && schema.enum.length > 0) {
		return (
			<SelectInput
				name={name}
				schema={schema}
				value={value}
				onChange={onChange}
				required={required}
			/>
		);
	}

	// Type-based dispatch (existing)
	switch (schema.type) {
		case "string":
			return (
				<StringInput
					name={name}
					schema={schema}
					value={value}
					onChange={onChange}
					required={required}
				/>
			);
		case "number":
		case "integer":
			return (
				<NumberInput
					name={name}
					schema={schema}
					value={value}
					onChange={onChange}
					required={required}
				/>
			);
		case "boolean":
			return (
				<BooleanToggle
					name={name}
					schema={schema}
					value={value}
					onChange={onChange}
					required={required}
				/>
			);
		case "object":
			return (
				<ObjectFields
					name={name}
					schema={schema}
					value={value}
					onChange={onChange}
					required={required}
					SchemaField={SchemaField}
					templateVariables={templateVariables}
				/>
			);
		case "array":
			return (
				<ArrayFields
					name={name}
					schema={schema}
					value={value}
					onChange={onChange}
					required={required}
					SchemaField={SchemaField}
					templateVariables={templateVariables}
				/>
			);
		default:
			return (
				<StringInput
					name={name}
					schema={schema}
					value={value}
					onChange={onChange}
					required={required}
				/>
			);
	}
}
